<template>
  <div class="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
    <div class="flex items-center space-x-3">
      <!-- Sync Status Icon -->
      <div class="flex items-center">
        <div
          :class="[
            'w-2 h-2 rounded-full mr-2',
            syncing ? 'bg-blue-500 animate-pulse' : (lastSyncAt ? 'bg-green-500' : 'bg-gray-300')
          ]" />
        <span class="text-sm text-gray-600">
          <span v-if="syncing">Syncing...</span>
          <span v-else-if="lastSyncAt">Last synced {{ formatTimeAgo(lastSyncAt) }}</span>
          <span v-else>Not synced yet</span>
        </span>
      </div>

      <!-- Category Sync Details (expandable) -->
      <button
        v-if="categorySyncStatus && Object.keys(categorySyncStatus).length > 0"
        @click="showDetails = !showDetails"
        class="text-xs text-gray-400 hover:text-gray-600 flex items-center">
        <svg
          :class="['w-4 h-4 transition-transform', showDetails ? 'rotate-180' : '']"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>

    <!-- Refresh Button -->
    <button
      @click="refresh"
      :disabled="syncing || refreshCooldown"
      class="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed">
      <svg
        :class="['w-4 h-4 mr-1', syncing ? 'animate-spin' : '']"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      {{ syncing ? 'Syncing' : 'Refresh' }}
    </button>
  </div>

  <!-- Expanded Category Details -->
  <transition name="slide">
    <div v-if="showDetails" class="px-4 py-2 bg-gray-50 border-b border-gray-200">
      <div class="grid grid-cols-3 gap-4 text-xs">
        <div v-for="(status, category) in categorySyncStatus" :key="category">
          <span class="font-medium text-gray-700 capitalize">{{ category }}</span>
          <span class="text-gray-500 ml-2">
            {{ status.lastSyncAt ? formatTimeAgo(status.lastSyncAt) : 'Never' }}
          </span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SyncStatusBar',

  props: {
    syncing: {
      type: Boolean,
      default: false
    },
    lastSyncAt: {
      type: [String, Date],
      default: null
    },
    categorySyncStatus: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['refresh'],

  data() {
    return {
      showDetails: false,
      refreshCooldown: false
    }
  },

  methods: {
    refresh() {
      if (this.syncing || this.refreshCooldown) return

      this.$emit('refresh')

      // Debounce: prevent rapid refreshes
      this.refreshCooldown = true
      setTimeout(() => {
        this.refreshCooldown = false
      }, 5000)
    },

    formatTimeAgo(dateString) {
      if (!dateString) return 'Never'

      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffSecs < 60) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays === 1) return 'yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
